import React, { useEffect } from 'react';
import {
  SkeletonBlock,
  SkeletonLine,
  SkeletonContent,
} from '@one-thd/sui-atomic-components';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { useAddress } from '../../hooks/use-address';
import { useDrawer } from '../../hooks/use-drawer';
import {
  ADD_DELIVERY_ADDRESS,
  INITIAL_ENROLLMENT,
  SELECT_DELIVERY_ADDRESS,
} from '../../models/views';

import { InstantCheckoutDrawer } from '../InstantCheckoutDrawer/InstantCheckoutDrawer';
import { SetupInstantCheckout } from './SetupInstantCheckout';
import { useInstantCheckoutStatus } from '../../hooks/use-instant-checkout-status';
import { usePayments } from '../../hooks/use-payments';
import { InstantCheckoutToggle } from './InstantCheckoutToggle/InstantCheckoutToggle';
import { TAlertType } from '../../models/common/alert';
import { InstantCheckoutAlert } from '../shared/InstantCheckoutAlert';
import { RecaptchaAction } from '../../constants/recaptcha';
import { track } from '../../analytics/analytics';
import { INSTANT_CHECKOUT_OVERLAY } from '../../constants/analytics';

export const InstantCheckoutInfo = ({
  getRecaptchaToken,
}: {
  getRecaptchaToken: (action: RecaptchaAction) => Promise<string>;
}) => {
  const { openDrawer, hideDrawer } = useDrawer();
  const { hasAddresses, addressLoading, defaultAddress } = useAddress();
  const {
    profileLoading,
    isEnrolledInInstantCheckout,
    alertMessage,
    timesLoaded,
    setTimesLoaded,
  } = useInstantCheckoutStatus();
  const { paymentsLoading, defaultPayment } = usePayments();

  const handleStartSetUp = () => {
    const initialView = hasAddresses()
      ? SELECT_DELIVERY_ADDRESS
      : ADD_DELIVERY_ADDRESS;

    const initialAction = hasAddresses() ? 'select' : 'add';

    track(INSTANT_CHECKOUT_OVERLAY, {
      value: `instant checkout ${initialAction} delivery address`,
    });

    openDrawer(InstantCheckoutDrawer, {
      hideDrawer,
      initialView,
      flowName: INITIAL_ENROLLMENT,
    });
  };

  useEffect(() => {
    if (!addressLoading && !profileLoading && !paymentsLoading) {
      setTimesLoaded(timesLoaded + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressLoading, profileLoading, paymentsLoading]);

  const renderComponent = () => {
    if (addressLoading || profileLoading || paymentsLoading) {
      return (
        <SkeletonContent disablePadding>
          <SkeletonBlock height={20} />
          <SkeletonLine variant="multi" numberOfLines={3} />
        </SkeletonContent>
      );
    }
    if (
      !defaultAddress &&
      !defaultPayment &&
      !isEnrolledInInstantCheckout &&
      timesLoaded === 1
    ) {
      return <SetupInstantCheckout handleStartSetUp={handleStartSetUp} />;
    }
    return (
      <QueryProvider cacheKey="instantCheckout">
        <InstantCheckoutToggle getRecaptchaToken={getRecaptchaToken} />
      </QueryProvider>
    );
  };

  return (
    <>
      {alertMessage?.alertType === TAlertType.GENERAL && (
        <InstantCheckoutAlert />
      )}
      {renderComponent()}
    </>
  );
};
